(() => {
  'use strict';
  const readFavorites = () => {
    try { return JSON.parse(localStorage.getItem('ear_config_favorites')) || {}; } catch { return {}; }
  };
  const productSelector = '[data-sales-product]';
  let merchandisingRequest;
  function rankProducts(products, favorites, merchandising) {
    if (!merchandising) return window.SolsticePersonalizationCore.salesOrder(products, favorites);
    const sold = new Set(merchandising.soldProducts.map(String));
    const legacy = new Map(merchandising.legacyOrder.map((id,index)=>[String(id),index]));
    const isFavorite = product => Boolean(favorites[product.id]) || (product.variants || []).some(id=>Boolean(favorites[id]));
    return [...products].sort((a,b)=>{
      const favorite = Number(isFavorite(b))-Number(isFavorite(a));
      if (favorite) return favorite;
      const signature = Number(b.signature === true)-Number(a.signature === true);
      if (signature) return signature;
      if (a.signature && b.signature) return a._rank-b._rank;
      const seller = Number(sold.has(String(b.id)))-Number(sold.has(String(a.id)));
      if (seller) return seller;
      if (sold.has(String(a.id))) return a._rank-b._rank;
      return (legacy.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER)-(legacy.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER) || a._rank-b._rank;
    });
  }
  window.SolsticeCollectionRanking = {rankProducts};

  class CollectionOrder {
    constructor(grid) {
      this.grid = grid;
      this.page = Number(grid.dataset.page);
      this.pageSize = Number(grid.dataset.pageSize);
      this.nativeOrder = grid.dataset.defaultSort === 'manual' || grid.dataset.sort === 'manual';
      this.url = new URL(location.href);
      this.url.searchParams.delete('section_id');
      this.url.searchParams.delete('view');
      this.url.searchParams.delete('page');
      this.url.searchParams.set('sort_by', this.url.searchParams.get('sort_by') || grid.dataset.sort || 'best-selling');
      this.cards = new Map();
      this.pageRequests = new Map();
      this.layout = [...grid.children].map(node => node.matches(productSelector) ? null : node);
      this.capture(grid);
      this.pageRequests.set(this.page, Promise.resolve());
      this.revision = 0;
      this.onChange = () => this.apply();
      this.onStorage = event => { if (event.key === 'ear_config_favorites' || event.key === null) this.apply(); };
      window.addEventListener('favorites-updated', this.onChange);
      window.addEventListener('storage', this.onStorage);
      this.grid.closest('#ProductGridContainer').addEventListener('solstice:composition-filtered', this.onChange);
      this.apply(window.SolsticeCollectionInitialOrder).finally(() => window.SolsticeCollectionInitialOrder?.finish());
    }

    capture(root) {
      root.querySelectorAll(productSelector).forEach(card => this.cards.set(card.dataset.salesProduct, card));
    }

    async fetch(url, json = false) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) throw new Error('Collection unavailable');
        return json ? await response.json() : await response.text();
      } finally { clearTimeout(timer); }
    }

    products() {
      if (!this.productsRequest) {
        this.productsRequest = (async () => {
          const result = [];
          let pages = 1;
          for (let page = 1; page <= pages; page++) {
            const url = new URL(this.url);
            url.searchParams.set('view', 'sales-order');
            url.searchParams.set('ranking_version', '20261025');
            url.searchParams.set('page', String(page));
            const data = await this.fetch(url, true);
            if (data.page !== page || !Array.isArray(data.products)) throw new Error('Invalid collection data');
            pages = data.pages;
            result.push(...data.products);
          }
          return result;
        })().catch(error => { this.productsRequest = null; throw error; });
      }
      return this.productsRequest;
    }

    merchandising() {
      if (this.nativeOrder) return Promise.resolve(null);
      if (this.url.searchParams.get('sort_by') !== 'best-selling') return Promise.resolve(null);
      if (!merchandisingRequest) {
        merchandisingRequest = this.fetch('https://stockback-production-ca15.up.railway.app/api/collection-ranking', true).then(data=>{
          if (data.version !== 1 || !Array.isArray(data.legacyOrder) || !Array.isArray(data.soldProducts)) throw new Error('Invalid ranking data');
          return data;
        }).catch(()=>{merchandisingRequest=null;return null;});
      }
      return merchandisingRequest;
    }

    loadPage(page) {
      if (!this.pageRequests.has(page)) {
        const url = new URL(this.url);
        url.searchParams.set('section_id', this.grid.dataset.id);
        url.searchParams.set('page', String(page));
        const request = this.fetch(url).then(html => {
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sales-grid]');
          if (!source || Number(source.dataset.page) !== page || Number(source.dataset.pageSize) !== this.pageSize) throw new Error('Invalid collection page');
          source.querySelectorAll('script').forEach(script => script.remove());
          this.capture(source);
        }).catch(error => { this.pageRequests.delete(page); throw error; });
        this.pageRequests.set(page, request);
      }
      return this.pageRequests.get(page);
    }

    async apply(initialOrder) {
      if (!this.grid.isConnected) {
        window.removeEventListener('favorites-updated', this.onChange);
        window.removeEventListener('storage', this.onStorage);
        return;
      }
      const revision = ++this.revision;
      const favorites = readFavorites();
      const defaultSort = !this.nativeOrder && this.url.searchParams.get('sort_by') === 'best-selling';
      if (!defaultSort && !Object.keys(favorites).some(id => favorites[id]) && !this.personalized) return;
      try {
        const [products, merchandising] = await Promise.all([this.products(), this.merchandising()]);
        if (revision !== this.revision || !this.grid.isConnected) return;
        this.capture(this.grid);
        const ordered = rankProducts(products, favorites, merchandising);
        const filter = this.grid.closest('#ProductGridContainer').querySelector('composition-placement-filter');
        // Placement filtering expands the composition grid to every page.
        const allLoaded = filter?.allLoaded === true;
        const offset = (this.page - 1) * this.pageSize;
        const visible = allLoaded ? ordered : ordered.slice(offset, offset + this.pageSize);
        const needed = new Set(visible.filter(p => !this.cards.has(String(p.id))).map(p => Math.floor(p._rank / this.pageSize) + 1));
        await Promise.all([...needed].map(page => this.loadPage(page)));
        if (revision !== this.revision || !this.grid.isConnected || filter?.loading) return;
        if (visible.some(p => !this.cards.has(String(p.id)))) throw new Error('Missing collection cards');
        if (initialOrder?.expired) return;
        const nodes = visible.map(p => this.cards.get(String(p.id)));
        const fragment = document.createDocumentFragment();
        let index = 0;
        for (const slot of this.layout) {
          const node = slot || nodes[index++];
          if (node) fragment.append(node);
        }
        while (index < nodes.length) fragment.append(nodes[index++]);
        if (allLoaded) {
          const placement = filter.field.value;
          nodes.forEach(node => { node.hidden = Boolean(placement && !node.dataset.placements.includes(`|${placement}|`)); });
        }
        this.grid.replaceChildren(fragment);
        this.personalized = true;
        this.grid.dataset.salesOrderReady = 'true';
        window.solsticeCardMedia?.scan(this.grid);
        const color = this.grid.closest('#ProductGridContainer').querySelector('[data-color-family]:checked')?.dataset.colorFamily;
        if (color) window.dispatchEvent(new CustomEvent('global-color-switch', {detail: {color}}));
        window.dispatchEvent(new Event('solstice:collection-ranked'));
      } catch (error) {
        // Preserve the complete server-rendered page on a slow or failed request.
        console.warn('Collection favorites could not be ordered', error);
      }
    }
  }

  const init = () => document.querySelectorAll('[data-sales-grid]').forEach(grid => {
    if (!grid.solsticeOrder) grid.solsticeOrder = new CollectionOrder(grid);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
